import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

const C = { espresso: "#280B03", cream: "#FBF8F5", terracotta: "#985E4B", tan: "#C2AA9D", maroon: "#53271E" };
const LOGO_URL = "https://www.maisoncore.nl/logo/maison-core-logo-onyx.png";

const DEFAULTS = [
  { id: "algemeen", label: "Algemeen", name: "Team Maison Core", title: "Reformer Pilates Studio", email: "info@maisoncore.nl", phone: "+31 6 42 33 54 59", instagram: "https://www.instagram.com/maisoncore.studio/", linkedin: "https://www.linkedin.com/company/maisoncore" },
  { id: "hajiba", label: "Hajiba", name: "Hajiba de Nobrega", title: "Founder", email: "info@maisoncore.nl", phone: "+31 6 42 33 54 59", instagram: "https://www.instagram.com/maisoncore.studio/", linkedin: "https://www.linkedin.com/company/maisoncore" },
  { id: "yrishar", label: "Yrishar", name: "Yrishar de Nobrega", title: "Co-Founder", email: "yrishar@maisoncore.nl", phone: "+31 6 42 33 54 59", instagram: "https://www.instagram.com/maisoncore.studio/", linkedin: "https://www.linkedin.com/company/maisoncore" },
];

function useNoIndex(title) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => {
      document.title = prevTitle;
      document.head.removeChild(meta);
    };
  }, [title]);
}

function signatureHtml(d) {
  const link = (href, label) => `<a href="${href}" target="_blank" style="color:${C.terracotta};text-decoration:none;">${label}</a>`;
  return `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,Helvetica,sans-serif;color:${C.espresso};">
  <tr>
    <td width="180" style="padding:0 28px 0 0;vertical-align:middle;width:180px;">
      <a href="https://www.maisoncore.nl" target="_blank" style="text-decoration:none;">
        <img src="${LOGO_URL}" alt="Maison Core. Reformer Studio" width="180" style="display:block;width:180px;max-width:180px;height:auto;border:0;" />
      </a>
    </td>
    <td style="border-left:1px solid ${C.tan};padding:4px 0 4px 28px;vertical-align:middle;">
      <div style="font-size:15px;font-weight:bold;letter-spacing:0.3px;color:${C.espresso};">${d.name}</div>
      <div style="font-size:11px;letter-spacing:2.5px;text-transform:uppercase;color:${C.terracotta};margin:3px 0 10px;">${d.title}</div>
      <div style="font-size:12px;line-height:20px;color:${C.espresso};">
        <span style="white-space:nowrap;">${link(`tel:${d.phone.replace(/\s/g, "")}`, d.phone)}</span>&nbsp;&nbsp;·&nbsp;&nbsp;${link(`mailto:${d.email}`, d.email)}<br/>
        Buitenveldertselaan 120, 1081 AB Amsterdam
      </div>
      <div style="border-top:1px solid ${C.tan};margin-top:10px;padding-top:9px;font-size:11px;letter-spacing:1.5px;text-transform:uppercase;">
        ${link("https://www.maisoncore.nl", "maisoncore.nl")}&nbsp;&nbsp;·&nbsp;&nbsp;${link(d.instagram, "Instagram")}&nbsp;&nbsp;·&nbsp;&nbsp;${link(d.linkedin, "LinkedIn")}
      </div>
    </td>
  </tr>
</table>`;
}

const FIELDS = [
  { key: "name", label: "Naam" },
  { key: "title", label: "Functie" },
  { key: "email", label: "E-mail" },
  { key: "phone", label: "Telefoon" },
  { key: "instagram", label: "Instagram-URL" },
  { key: "linkedin", label: "LinkedIn-URL" },
];

export default function Signature() {
  useNoIndex("Maison Core — E-mailhandtekeningen");
  const [active, setActive] = useState("algemeen");
  const [data, setData] = useState(() => Object.fromEntries(DEFAULTS.map((d) => [d.id, { ...d }])));
  const d = data[active];
  const html = useMemo(() => signatureHtml(d), [d]);

  const update = (key, value) => setData((prev) => ({ ...prev, [active]: { ...prev[active], [key]: value } }));

  const copySignature = async () => {
    try {
      await navigator.clipboard.write([
        new window.ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([`${d.name} — ${d.title}\n${d.phone} · ${d.email}\nBuitenveldertselaan 120, 1081 AB Amsterdam\nwww.maisoncore.nl`], { type: "text/plain" }),
        }),
      ]);
      toast.success("Handtekening gekopieerd — plak hem in Gmail of Outlook");
    } catch {
      await navigator.clipboard.writeText(html);
      toast.success("HTML gekopieerd naar klembord");
    }
  };

  const copyHtml = async () => {
    await navigator.clipboard.writeText(html);
    toast.success("HTML-code gekopieerd");
  };

  return (
    <div className="min-h-screen px-4 py-12 sm:px-8 sm:py-20" style={{ background: C.espresso }} data-testid="signature-page">
      <div className="mx-auto max-w-3xl">
        <p className="label" style={{ color: C.tan, letterSpacing: 4, textTransform: "uppercase", fontSize: 12 }}>Maison Core — intern</p>
        <h1 className="mt-4 font-serif text-4xl sm:text-5xl" style={{ color: C.cream }}>E-mailhandtekeningen</h1>
        <p className="mt-4 max-w-xl font-sans text-sm leading-relaxed" style={{ color: `${C.cream}99` }}>
          Kies een handtekening, pas de gegevens aan en klik op "Kopieer handtekening". Plak hem daarna in Gmail (Instellingen → Handtekening) of Outlook.
        </p>

        <div className="mt-10 flex gap-2" data-testid="signature-tabs">
          {DEFAULTS.map((p) => (
            <button
              key={p.id}
              onClick={() => setActive(p.id)}
              className="label px-5 py-3 transition-colors duration-300"
              style={active === p.id ? { background: C.cream, color: C.espresso } : { background: "transparent", color: C.cream, border: `1px solid ${C.tan}55` }}
              data-testid={`signature-tab-${p.id}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        <div className="mt-8 grid gap-8 lg:grid-cols-[320px,1fr]">
          <div className="space-y-4" data-testid="signature-fields">
            {FIELDS.map((f) => (
              <label key={f.key} className="block">
                <span className="label block text-xs" style={{ color: C.tan, letterSpacing: 2, textTransform: "uppercase" }}>{f.label}</span>
                <input
                  value={d[f.key]}
                  onChange={(e) => update(f.key, e.target.value)}
                  className="mt-2 w-full bg-transparent px-4 py-3 font-sans text-sm focus:outline-none"
                  style={{ border: `1px solid ${C.tan}55`, color: C.cream }}
                  data-testid={`signature-input-${f.key}`}
                />
              </label>
            ))}
          </div>

          <div>
            <p className="label text-xs" style={{ color: C.tan, letterSpacing: 2, textTransform: "uppercase" }}>Voorbeeld</p>
            <div className="mt-3 p-8" style={{ background: "#ffffff" }} data-testid="signature-preview">
              <div dangerouslySetInnerHTML={{ __html: html.replaceAll("https://www.maisoncore.nl/logo/", "/logo/") }} />
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <button onClick={copySignature} className="label px-7 py-4 transition-colors duration-300 hover:opacity-90" style={{ background: C.terracotta, color: C.cream }} data-testid="signature-copy-btn">
                Kopieer handtekening
              </button>
              <button onClick={copyHtml} className="label px-7 py-4 transition-colors duration-300" style={{ border: `1px solid ${C.tan}`, color: C.cream }} data-testid="signature-copy-html-btn">
                Kopieer HTML-code
              </button>
            </div>
            <p className="mt-5 font-sans text-xs leading-relaxed" style={{ color: `${C.cream}66` }}>
              Tip: in Gmail → Instellingen → Alle instellingen → Handtekening → plak met Cmd/Ctrl+V. De opmaak blijft behouden.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
